import { Link } from "gatsby";
import gsap from "gsap";
import React, { useEffect, useRef } from "react";
import { Col, Container, Row } from "react-bootstrap";
import Button from "./Button"; // Custom Button component
import { SafeHtmlParser } from "./SafeHtmlParser";
import useOnScreen from "./use-on-screen"; // Import the hook

const IconsLayout1 = ({ heading, body, button, items }) => {
	// References for the outer container (scroll trigger) and left column
	const containerRef = useRef(null);
	const leftContainerRef = useRef(null);

	// Trigger animations when the section scrolls into view (60px offset)
	const isVisible = useOnScreen(containerRef, "10px");

	useEffect(() => {
		if (isVisible) {
			// Animate each left item sequentially.
			// Each element rotates from 90° (flat) to 0°,
			// with the transform origin set to "-40px 100%" so they all pivot around the same hidden point.
			const leftItems = gsap.utils.toArray(".left-item");
			gsap.from(leftItems, {
				duration: 1,
				rotation: 5, // Changed from -90 to 90 so they come up instead of going down
				transformOrigin: "-40px 100%", // Pivot point remains the same
				opacity: 0,
				ease: "power2.out",
				stagger: 0.3,
				delay: 1,
			});

			// Animate the right-hand side items (both desktop and mobile) by fading them in
			const rightAnimateEls = gsap.utils.toArray(".right-animate");
			rightAnimateEls.forEach((el) => {
				gsap.from(el.children, {
					opacity: 0,
					duration: 1,
					stagger: 0.2,
					ease: "power2.out",
					delay: 0.4,
				});
			});
		}
	}, [isVisible]);

	// Hover effects for the link text using GSAP
	const handleMouseEnter = (e, colour) => {
		gsap.to(e.target, { color: colour, duration: 0.1 });
	};

	const handleMouseLeave = (e) => {
		gsap.to(e.target, { color: "#000", duration: 0.1 });
	};

	// Custom padding and margin classes for the staggered right-hand layout
	const paddingClasses = [
		"ps-0",
		"ps-md-5",
		"ps-lg-8 ps-md-7",
		"ps-md-5",
		"ps-0",
	];
	const marginClasses = ["mb-5", "mb-5", "mb-5", "mb-5", "mb-0"];

	return (
		<section className="py-5 py-lg-7" ref={containerRef}>
			<Container>
				<Row className="align-items-center gx-lg-5">
					{/* Left-hand side: Heading, Body, and Button */}
					<Col md={6} lg={7}>
						{/* Each left-side element gets its own wrapper with the "left-item" class */}
						<div className="left-animate" ref={leftContainerRef}>
							<div className="text-primary left-item">
								<SafeHtmlParser htmlContent={heading} />
							</div>
							<div className="mb-3 left-item">
								<SafeHtmlParser htmlContent={body} />
							</div>
							{button && (
								<div className="left-item">
									<Button
										width="w-100 w-md-auto"
										colour="#00304B"
										text={button.title}
										link={button.url}
										isOutline={true}
									/>
								</div>
							)}
						</div>
					</Col>

					{/* Right-hand side: Icons with Descriptions */}
					{/* Desktop Version */}
					<Col className="d-none d-md-block" md={6} lg={5}>
						<ul className="right-animate" style={iconListStyles}>
							{items.map((item, index) => (
								<li
									key={index}
									className={`${marginClasses[index]} ${paddingClasses[index]}`}
									style={iconItemStyles}
								>
									<img
										src={item.icon.sourceUrl}
										alt={item.heading}
										style={iconStyles}
									/>
									<Link
										className="fs-4 outfit-regular"
										to={item.url}
										onMouseEnter={(e) => handleMouseEnter(e, item.colour)}
										onMouseLeave={handleMouseLeave}
										style={linkStyles}
									>
										{item.heading}
									</Link>
								</li>
							))}
						</ul>
					</Col>

					{/* Mobile Version */}
					<Col className="pt-4 d-md-none text-center" md={6} lg={5}>
						<Row className="g-4 justify-content-center right-animate">
							{items.map((item, index) => (
								<Col key={index} className="d-block px-5" xs={6}>
									<img
										src={item.icon.sourceUrl}
										alt={item.heading}
										className="mb-3"
										style={{ width: "50px" }}
									/>
									<Link
										className="fs-4 d-block outfit-regular"
										to={item.url}
										onMouseEnter={(e) => handleMouseEnter(e, item.colour)}
										onMouseLeave={handleMouseLeave}
										style={linkStyles}
									>
										{item.heading}
									</Link>
								</Col>
							))}
						</Row>
					</Col>
				</Row>
			</Container>
		</section>
	);
};

// Inline styles
const iconListStyles = {
	listStyleType: "none",
	padding: 0,
	margin: 0,
};

const iconItemStyles = {
	display: "flex",
	alignItems: "center",
};

const iconStyles = {
	minWidth: "70px",
	maxWidth: "70px",
	marginRight: "1rem",
};

const linkStyles = {
	fontSize: "1.25rem",
	textDecoration: "none",
	color: "#000",
};

export default IconsLayout1;
